/**
 * What one street segment would take to be served by a district network.
 *
 * Ported from the legacy hover tooltip, which built ~120 lines of HTML string
 * per mousemove. The figures are the same; what changed is that the numbers are
 * read through lib/props and formatted through lib/tramos, so "kWh in the
 * payload, MWh on screen" is stated once rather than in six template literals.
 *
 * The 4G/5G comparison only appears for a segment where both are viable. On any
 * other segment there is no decision to present, and showing the panel anyway
 * was what made the legacy tooltip read as if every street had a choice.
 */

import { fmt } from "./lib/format";
import { count, num, str } from "./lib/props";
import { VIAB_COLORS, VIAB_LABELS, lcoe, mwh, viabilidadOf } from "./lib/tramos";
import type { Anchor } from "./HoverCard";
import { HoverCard } from "./HoverCard";

interface Props {
  anchor: Anchor | null;
  props: Record<string, unknown> | null;
}

/** Linear density, the figure the 1,5 MWh/m·año threshold is applied to. */
function density(value: unknown): string {
  return mwh(num(value), 2);
}

export function TramoPopup({ anchor, props: p }: Props) {
  if (!p) return null;

  const viab = viabilidadOf(p["viabilidad"]);
  const tCalef = num(p["t_calef_avg"]);
  const tRefrig = num(p["t_refrig_avg"]);
  const acsBoost = count(p["pct_acs_boost"]);
  const densCalACS = density(p["dens_calef_acs_kwh_m"]);

  return (
    <HoverCard anchor={anchor} width={300} className="tramo-card">
      <h2 className="card-title">{str(p["nombre"]) ?? "(sin nombre)"}</h2>

      <span className="viab-badge" style={{ background: VIAB_COLORS[viab] }}>
        {VIAB_LABELS[viab].toUpperCase()}
      </span>

      <div className="card-pair">
        <div>
          <b>Edificios</b>
          <br />
          {fmt(num(p["n_edificios"]))} ({fmt(num(p["n_viviendas"]))} viv.)
        </div>
        <div>
          <b>Longitud</b>
          <br />
          {fmt(num(p["longitud_m"]), 0, "m")}
        </div>
      </div>

      <section className="card-block">
        <h3>Demandas anuales</h3>
        <div className="card-triple">
          <div><span className="dot warm">●</span> calef <b>{mwh(num(p["dem_calef_kwh"]))}</b> MWh</div>
          <div><span className="dot cool">●</span> ACS <b>{mwh(num(p["dem_acs_kwh"]))}</b> MWh</div>
          <div><span className="dot accent">●</span> frío <b>{mwh(num(p["dem_refrig_kwh"]))}</b> MWh</div>
        </div>
        <p className="card-foot">
          densidad lineal: calor+ACS <b>{densCalACS}</b> · frío{" "}
          <b>{density(p["dens_refrig_kwh_m"])} MWh/m·año</b>
        </p>
      </section>

      <section className="card-block">
        <h3>T impulsión necesaria (media de los edificios)</h3>
        <div className="card-pair">
          <div>
            calefacción{" "}
            {tCalef == null ? "–" : (
              <>
                <b className="warm">{tCalef}°C</b>
                <span className="card-range">
                  {" "}({fmt(num(p["t_calef_min"]))}–{fmt(num(p["t_calef_max"]))})
                </span>
              </>
            )}
          </div>
          <div>
            refrigeración {tRefrig == null ? "–" : <b className="cool">{tRefrig}°C</b>}
          </div>
        </div>
        {acsBoost > 0 && (
          <p className="card-foot warm">
            {acsBoost}% edif. necesitan HP local para ACS (T calef &lt; 60°C)
          </p>
        )}
      </section>

      {viab === "ambos" && (
        <section className="viab-compare" style={{ borderColor: VIAB_COLORS.ambos }}>
          <h3 style={{ color: VIAB_COLORS.ambos }}>Comparativa 4G vs 5G</h3>
          <div className="card-pair">
            <div className="viab-option" style={{ background: `${VIAB_COLORS["4g"]}18` }}>
              <b style={{ color: VIAB_COLORS["4g"] }}>4G · Red calor</b>
              <span className="viab-metric">densidad</span>
              <b>{densCalACS} MWh/m·año</b>
              <p>
                ✓ supera umbral 1,5 MWh/m·año<br />
                LCOE no calculado<br />
                T impulsión {tCalef == null ? "–" : `${tCalef}°C`}
              </p>
            </div>
            <div className="viab-option" style={{ background: `${VIAB_COLORS["5g"]}18` }}>
              <b style={{ color: VIAB_COLORS["5g"] }}>5G · Lazo geo</b>
              <span className="viab-metric">LCOE geo</span>
              <b>{lcoe(num(p["lcoe_geo"]))}</b>
              <p>
                LCOE aero {lcoe(num(p["lcoe_aero"]))}<br />
                ✓ geo &lt; aero<br />
                T lazo ~10–15°C
              </p>
            </div>
          </div>
          <p className="card-foot italic">
            Requiere estudio de detalle para decidir entre redes.
          </p>
        </section>
      )}

      <hr />
      <p className="card-foot">
        LCOE geo {lcoe(num(p["lcoe_geo"]))} · aero {lcoe(num(p["lcoe_aero"]))}<br />
        <b className="accent">Ahorro</b>{" "}
        {mwh(num(p["ahorro_coste"]))} k€/año · {mwh(num(p["ahorro_emisiones"]))} tCO₂/año<br />
        <span className="card-small">
          Geotermia suelo: {str(p["potencial_sup"]) ?? "–"} · vertical:{" "}
          {str(p["potencial_vert"]) ?? "–"}
        </span>
      </p>
    </HoverCard>
  );
}
